import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import eq from 'fast-deep-equal'
import {
  type RefObject,
  type SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import type { ResizableProps, ResizeCallbackData } from 'react-resizable'

import { STICKY_NOTE_SIZE } from '@/constants/constants'
import { useScaleZoom } from '@/hooks'
import type { Size } from '@/types/models'
import { IconResize } from '../icons'
import css from '../StickyNotesItem.module.scss'

// @types/react-resizable 1.7 predates transformScale; the installed 3.x takes it.
type UseResize = ResizableProps & {
  transformScale: number
  onContentUpdated: () => void
}

const MIN_WIDTH = 60

// How tall the note has to be for its text: from the top of the content to the bottom of
// the last paragraph, plus the inset the editable keeps under it. Measured on screen, so
// it comes divided by the zoom.
const getTextHeight = (
  contentRef: RefObject<HTMLDivElement | null>,
  scale: number
): number => {
  const content = contentRef.current
  const editor = content?.querySelector<HTMLElement>(
    '[data-slate-editor="true"]'
  )
  const lastBlock = editor?.lastElementChild

  if (!content || !editor || !lastBlock) {
    return 0
  }

  const textBottom =
    lastBlock.getBoundingClientRect().bottom -
    content.getBoundingClientRect().top

  const inset = parseFloat(getComputedStyle(editor).paddingBottom) || 0

  return Math.ceil(textBottom / scale + inset)
}

const useResize = (
  contentRef: RefObject<HTMLDivElement | null>,
  id: string,
  disabled: boolean,
  isSelected: boolean
): UseResize => {
  const { updateStickyNoteSize } = useProjectLegacyActions()

  const { scale } = useScaleZoom()
  const stickyNotes = useSelector(
    migrationProjectSelector.getProjectStickyNotes
  )
  const note = useMemo(
    () =>
      stickyNotes.find((item) => item.id === id) ?? {
        id: '',
        size: {
          width: 0,
          height: 0,
        },
        offset: {
          x: 0,
          y: 0,
        },
        content: '',
        color: '',
        textSize: 's' as const,
      },
    [stickyNotes]
  )

  const [size, setSize] = useState<Size>(note?.size ?? STICKY_NOTE_SIZE)

  // The stored height is only the least the note is drawn at (useNoteStyle sets it as
  // min-height), so text typed past it, or a height guessed through the public API, makes
  // the note taller on screen than in the project, and the project is left as it is
  // (OCT-5523). The handle has to start from what is drawn -- from the stored height a
  // drag does nothing until it passes the text -- and the text is as far as it shrinks.
  const [textHeight, setTextHeight] = useState(0)

  const scaleRef = useRef(scale)
  scaleRef.current = scale

  const measureText = () => {
    setTextHeight(getTextHeight(contentRef, scaleRef.current))
  }

  // The content box follows the drawn note, so it resizes whenever the text outgrows the
  // stored height, gives it back, or reflows to a new width.
  useLayoutEffect(() => {
    const content = contentRef.current

    if (!content) {
      return
    }

    const observer = new ResizeObserver(measureText)
    observer.observe(content)

    return () => observer.disconnect()
  }, [])

  // Text that still fits inside the stored height changes nothing the observer sees.
  useLayoutEffect(measureText, [note.content, note.textSize])

  const onResizeStart = (e: SyntheticEvent, _data: ResizeCallbackData) => {
    e.stopPropagation()
  }

  const onResizeStop = (e: SyntheticEvent, data: ResizeCallbackData) => {
    if (!eq(data.size, note?.size)) {
      e.stopPropagation()
    }

    if (note) {
      const payload = {
        id: note.id,
        size: data.size,
      }

      updateStickyNoteSize(payload)
    }
  }

  const onResize = (_e: SyntheticEvent, data: ResizeCallbackData) => {
    setSize(data.size)
  }

  // The editor reports a change before it has drawn it.
  const onContentUpdated = () => {
    requestAnimationFrame(measureText)
  }

  // update size if changed from outer side
  useEffect(() => {
    if (note?.size && !eq(note.size, size) && !isSelected) {
      setSize(note.size)
    }
  }, [note])

  return {
    onResize,
    onResizeStart,
    onResizeStop,
    onContentUpdated,
    minConstraints: [MIN_WIDTH, textHeight],
    width: size.width,
    height: Math.max(size.height, textHeight),
    // The handle moves in screen pixels and the note is sized in canvas pixels; without the
    // zoom the edge runs at zoom× the pointer's speed (OCT-5528).
    transformScale: scale,
    handle: !disabled && (
      <div className={cx(css.resizeHandle, 'sticky-note-resize-handle')}>
        <i>
          <IconResize />
        </i>
      </div>
    ),
  }
}

export default useResize
